import { useEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, AudioLines, Check, CheckCircle2, ChevronUp, Clock, Mail, MessageSquare, Mic, MicOff, Pencil, Phone, Plus, X, XCircle, Zap } from 'lucide-react';
import { SalesFace } from '../NexusFace';
import { actOnSalesPlay, ensureDirectChat, listBots, listChats, type SalesPlayParts } from '../agents/api';
import type { SalesPlay } from '../../hooks/useSalesPlays';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useVoiceHelp } from '../../contexts/VoiceHelpContext';
import type { CrmRequiredActionItem } from '../../lib/crm/requiredActions';
import { getNewLeadResponseSeverity } from '../../lib/crm/requiredActions';
import { hapticMedium } from '../../lib/haptics';
import { useLongPress } from '../../hooks/useLongPress';
import type { QuickActionsAnchor } from './CrmLeadQuickActionsMenu';
import LeadScoreThermometer from '../crm/LeadScoreThermometer';
import VoiceMeter from '../agents/VoiceMeter';
import { useSalesCardCall, useSalesCardWorking } from '../../lib/salesCardWork';

const CHANNEL: Record<SalesPlay['channel'], { noun: string; verb: string; icon: typeof Mail }> = {
  sms: { noun: 'Text', verb: 'Text', icon: MessageSquare },
  email: { noun: 'Email', verb: 'Email', icon: Mail },
  call: { noun: 'Call', verb: 'Call', icon: Phone },
  none: { noun: 'Move', verb: '', icon: ArrowRight },
};

function moneyLabel(amount: number): string {
  const whole = Math.abs(amount - Math.round(amount)) < 0.005;
  return whole
    ? `$${Math.round(amount).toLocaleString('en-US')}`
    : `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function followUpLabel(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return 'today';
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (date.toDateString() === tomorrow.toDateString()) return 'tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Why this lead is on the list, in the colour of its urgency. Names the CRM
 * action first (follow-up due, new lead, gone quiet) so the card reads as the
 * same row it replaced, with Sales' move underneath.
 */
function reasonFor(item: CrmRequiredActionItem, now: Date): { label: string; className: string; severity?: 'warning' | 'critical' } {
  if (item.kind === 'follow_up_due') {
    const when = item.dueAt ? formatDistanceToNow(new Date(item.dueAt), { addSuffix: true }) : null;
    return { label: when ? `Follow-up due · ${when}` : 'Follow-up due', className: 'text-red-600' };
  }
  if (item.kind === 'no_activity') {
    const when = item.lastActivityAt ? formatDistanceToNow(new Date(item.lastActivityAt)) : null;
    return { label: when ? `Gone quiet · ${when}` : 'Gone quiet', className: 'text-orange-600' };
  }
  if (item.kind === 'new_lead') {
    const severity = getNewLeadResponseSeverity(item.createdAt, now);
    return {
      label: `New lead · ${formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}`,
      className: severity === 'critical' ? 'text-red-600' : severity === 'warning' ? 'text-amber-600' : 'text-blue-600',
      severity: severity === 'none' ? undefined : severity,
    };
  }
  return { label: 'Waiting on you · next move', className: 'text-emerald-700' };
}

type Person = { id: string; name: string };

function crewFrom(ids: string[] | null | undefined, single: string | null): string[] {
  const list = (ids ?? []).filter(Boolean);
  if (list.length) return [...new Set(list)];
  return single ? [single] : [];
}

/** The schedule line without the person's name, once the chips show who is going. */
function scheduleLine(label: string | null, names: string[]): string | undefined {
  if (!label) return undefined;
  const bits = names.flatMap((name) => {
    const first = name.split(/\s+/)[0];
    return first && first !== name ? [name, first] : [name];
  }).filter(Boolean);
  for (const bit of bits) {
    const suffix = ` · ${bit}`;
    if (label.endsWith(suffix)) return label.slice(0, -suffix.length);
  }
  return label;
}

function stamp(date: string | null, time: string | null): string {
  return `${date || ''}T${(time || '').slice(0, 5)}`;
}

function windowsOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
  return Boolean(startA && endA && startB && endB) && startA < endB && startB < endA;
}

/** People already on the calendar across this window. Empty when the crew is free. */
async function busyNames(
  supabase: ReturnType<typeof useSupabase>,
  businessId: string,
  ids: string[],
  roster: Person[],
  startDate: string | null,
  startTime: string | null,
  endDate: string | null,
  endTime: string | null,
): Promise<string[]> {
  if (!supabase || !businessId || !ids.length || !startDate || !startTime) return [];
  const start = stamp(startDate, startTime);
  const end = stamp(endDate || startDate, endTime || startTime);
  const nameOf = (id: string) => roster.find((person) => person.id === id)?.name || 'Someone';
  const [{ data: jobs }, { data: visits }] = await Promise.all([
    supabase.from('bookings').select('id, title, start_date, start_time, end_date, end_time, status').eq('business_id', businessId).neq('status', 'cancelled').gte('start_date', startDate).lte('start_date', endDate || startDate).limit(40),
    supabase.from('project_field_quotes').select('id, resource_id, start_date, start_time, end_date, end_time, status').eq('business_id', businessId).in('status', ['scheduled', 'in progress']).gte('start_date', startDate).lte('start_date', endDate || startDate).limit(40),
  ]);
  const jobRows = jobs ?? [];
  const jobIds = jobRows.map((row) => row.id);
  const { data: assigned } = jobIds.length
    ? await supabase.from('booking_resources').select('booking_id, resource_id').in('booking_id', jobIds).in('resource_id', ids)
    : { data: [] as { booking_id: string; resource_id: string }[] };
  const busy = new Set<string>();
  for (const link of assigned ?? []) {
    const job = jobRows.find((row) => row.id === link.booking_id);
    if (!job) continue;
    const otherEnd = stamp(job.end_date || job.start_date, job.end_time || job.start_time);
    if (windowsOverlap(start, end, stamp(job.start_date, job.start_time), otherEnd)) busy.add(link.resource_id);
  }
  for (const visit of visits ?? []) {
    if (!visit.resource_id || !ids.includes(visit.resource_id)) continue;
    const otherEnd = stamp(visit.end_date || visit.start_date, visit.end_time || visit.start_time);
    if (windowsOverlap(start, end, stamp(visit.start_date, visit.start_time), otherEnd)) busy.add(visit.resource_id);
  }
  return [...busy].map(nameOf);
}

/** Add and remove the people on a staged booking or field quote. */
function CrewEditor({
  ids,
  roster,
  disabled,
  note,
  onChange,
}: {
  ids: string[];
  roster: Person[];
  disabled?: boolean;
  note?: string | null;
  onChange: (ids: string[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const nameOf = (id: string) => roster.find((person) => person.id === id)?.name || 'Someone';
  const available = roster.filter((person) => !ids.includes(person.id));
  return (
    <div>
      <div className="flex flex-wrap items-center gap-1">
        {ids.map((id) => (
          <span key={id} className="inline-flex h-6 items-center gap-0.5 rounded-full bg-gray-100 pl-2 pr-0.5 text-[12px] font-medium text-gray-800">
            {nameOf(id)}
            <button
              type="button"
              disabled={disabled || ids.length < 2}
              onClick={() => onChange(ids.filter((current) => current !== id))}
              className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-30"
              aria-label={`Remove ${nameOf(id)}`}
              title={ids.length < 2 ? 'Someone has to be on this' : `Remove ${nameOf(id)}`}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        ))}
        {available.length ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setAdding((open) => !open)}
            className="inline-flex h-6 items-center gap-0.5 rounded-full border border-dashed border-gray-300 px-2 text-[12px] font-medium text-gray-600 hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
            aria-expanded={adding}
          >
            <Plus className="h-3 w-3" aria-hidden />
            Add
          </button>
        ) : null}
      </div>
      {adding && available.length ? (
        <div className="mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
          {available.map((person) => (
            <button
              key={person.id}
              type="button"
              disabled={disabled}
              onClick={() => {
                onChange([...ids, person.id]);
                setAdding(false);
              }}
              className="block w-full px-2 py-1.5 text-left text-[12.5px] text-gray-800 hover:bg-gray-50"
            >
              {person.name}
            </button>
          ))}
        </div>
      ) : null}
      {note ? <p className="mt-1 text-[11.5px] leading-snug text-amber-700">{note}</p> : null}
    </div>
  );
}

/** One tickable line of the play. Unticked lines are left alone when the owner applies. */
function PartRow({
  checked,
  onToggle,
  disabled,
  label,
  detail,
  children,
}: {
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  label: string;
  detail?: string | null;
  children?: React.ReactNode;
}) {
  return (
    <li>
      <div className="flex items-start gap-2">
        <button
          type="button"
          role="checkbox"
          aria-checked={checked}
          disabled={disabled}
          onClick={onToggle}
          className={`mt-[3px] flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-[4px] border transition-colors touch-manipulation ${
            checked ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-gray-300 bg-white hover:border-gray-400'
          } disabled:opacity-50`}
          title={checked ? 'Leave this out' : 'Include this'}
        >
          {checked ? <Check className="h-3 w-3" strokeWidth={3} aria-hidden /> : null}
        </button>
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          className={`min-w-0 flex-1 text-left text-[12.5px] leading-snug ${checked ? 'text-gray-800' : 'text-gray-400 line-through'}`}
        >
          <span className="font-medium">{label}</span>
          {detail ? <span className={`${checked ? 'text-gray-500' : ''}`}> · {detail}</span> : null}
        </button>
      </div>
      {children ? <div className={`mt-1 pl-[23px] ${checked ? '' : 'opacity-50'}`}>{children}</div> : null}
    </li>
  );
}

/**
 * One card for a lead the Sales manager has a move staged on. Replaces the
 * plain lead row: the header is the lead (tap opens it, hold for quick
 * actions), the body is the play as a checklist (the touch, the stage move,
 * the follow-up, the close), every line ticked. The owner unticks what they
 * do not want and the button applies only what is left. Tap the draft to read
 * all of it; Edit (in the footer, once open) turns it into a text box.
 */
export default function SalesPlayCard({
  play,
  item,
  now,
  onOpenLead,
  onOpenQuote,
  onQuickActions,
  onActed,
  onSettled,
  onFailed,
}: {
  play: SalesPlay;
  item: CrmRequiredActionItem;
  now: Date;
  onOpenLead?: () => void;
  /** Opens the quote in the proposal details modal on this page. */
  onOpenQuote?: (proposalId: string) => void;
  onQuickActions?: (anchor: QuickActionsAnchor) => void;
  /** Fired the moment Send, Done, or Skip is pressed so the row can clear. */
  onActed: (playId: string, status: 'sent' | 'done' | 'skipped') => void;
  /** The request finished. The lead list can catch up. */
  onSettled?: (playId: string) => void;
  /** The request failed. The row comes back with this message. */
  onFailed?: (playId: string, message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(play.body);
  const [subject, setSubject] = useState(play.subject ?? '');
  const [error, setError] = useState<string | null>(play.send_error);
  const actingRef = useRef(false);
  useEffect(() => { setError(play.send_error); }, [play.send_error]);
  const [parts, setParts] = useState<SalesPlayParts>({ touch: true, stage: true, follow_up: true, mark: true, proposal: true, booking: true, field_quote: true });
  const [bookingCrew, setBookingCrew] = useState(() => crewFrom(play.booking_resource_ids, play.booking_resource_id));
  const [visitCrew, setVisitCrew] = useState(() => crewFrom(play.field_quote_resource_ids, play.field_quote_resource_id));
  const [roster, setRoster] = useState<Person[]>([]);
  const [bookingNote, setBookingNote] = useState<string | null>(null);
  const [visitNote, setVisitNote] = useState<string | null>(null);
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';
  const updating = useSalesCardWorking(play.opportunity_id, play.created_at);
  const onCall = useSalesCardCall(play.opportunity_id);
  const [talking, setTalking] = useState(false);
  const talkAboutThis = async () => {
    if (!businessId || talking) return;
    setTalking(true);
    setError(null);
    try {
      const [bots, chats] = await Promise.all([listBots(businessId), listChats(businessId)]);
      const sales = bots.find((bot) => bot.kind === 'sales' && !bot.archived_at && !bot.paused);
      if (!sales) {
        setError('Sales is not on for this business.');
        return;
      }
      const chat = await ensureDirectChat(businessId, sales, chats);
      window.dispatchEvent(new CustomEvent('assistant:startVoice', {
        detail: {
          surface: 'chat',
          botId: sales.id,
          chatId: chat.id,
          leadOpportunityId: play.opportunity_id,
          leadName: play.lead_name,
        },
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the call.');
    } finally {
      setTalking(false);
    }
  };

  const channel = CHANNEL[play.channel] ?? CHANNEL.sms;
  const isCall = play.channel === 'call';
  const isMove = play.channel === 'none';
  const isMessage = play.channel === 'sms' || play.channel === 'email';
  const mark = play.mark_status;
  const isWait = isMove && !mark && !play.move_to_stage;
  const hasBooking = Boolean(play.booking_label) && !play.booking_id;
  const hasFieldQuote = Boolean(play.field_quote_label) && !play.field_quote_id;
  const ChannelIcon = hasBooking || hasFieldQuote ? CheckCircle2 : mark === 'won' ? CheckCircle2 : mark === 'lost' ? XCircle : isWait ? Clock : channel.icon;
  const reason = reasonFor(item, now);
  const followUp = followUpLabel(play.next_follow_up_at);
  const edited = body !== play.body || subject !== (play.subject ?? '');
  const customer = item.customerName && item.customerName !== item.title ? item.customerName : null;
  // "TF Autobody (William)" → William; "Alex Eng" → Alex.
  // A lead named after the vehicle ("LandRover Defender") has no person to name: "Text them".
  const fullName = play.lead_name || item.customerName || '';
  const firstName = fullName && fullName !== item.title
    ? (fullName.match(/\(([^)]+)\)/)?.[1] || fullName).trim().split(/\s+/)[0]
    : 'them';
  const byline = hasBooking
    ? 'Booking staged by Sales'
    : hasFieldQuote
    ? 'Field quote staged by Sales'
    : play.proposal
    ? 'Proposal staged by Sales'
    : isCall
      ? 'Call prepped by Sales'
      : mark
        ? `Mark ${mark} · Sales`
        : isWait
          ? 'Nothing to send · Sales'
          : isMove
            ? 'Move staged by Sales'
            : `${channel.noun} drafted by Sales`;

  // Which lines the play has, and which are still ticked.
  const has = { touch: !isMove, stage: Boolean(play.move_to_stage), follow_up: Boolean(followUp), mark: Boolean(mark), proposal: Boolean(play.proposal), booking: hasBooking, field_quote: hasFieldQuote };
  const on = {
    touch: has.touch && parts.touch,
    stage: has.stage && parts.stage,
    follow_up: has.follow_up && parts.follow_up,
    mark: has.mark && parts.mark,
    proposal: has.proposal && parts.proposal !== false,
    booking: has.booking && parts.booking !== false,
    field_quote: has.field_quote && parts.field_quote !== false,
  };
  const anyOn = on.touch || on.stage || on.follow_up || on.mark || on.proposal || on.booking || on.field_quote;
  const toggle = (key: keyof SalesPlayParts) => setParts((current) => ({ ...current, [key]: !current[key] }));

  useEffect(() => {
    if (!businessId || (!hasBooking && !hasFieldQuote)) return;
    let cancel = false;
    void supabase.from('resources')
      .select('id, first_name, last_name, not_bookable, is_active')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .then(({ data }) => {
        if (cancel) return;
        const people = (data ?? [])
          .filter((row) => row.not_bookable !== true)
          .map((row) => ({ id: row.id, name: [row.first_name, row.last_name].filter(Boolean).join(' ') }))
          .filter((row) => row.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setRoster(people);
      });
    return () => { cancel = true; };
  }, [businessId, hasBooking, hasFieldQuote, supabase]);

  const saveCrew = (kind: 'booking' | 'field_quote', ids: string[]) => {
    if (!ids.length) return;
    if (kind === 'booking') setBookingCrew(ids);
    else setVisitCrew(ids);
    const patch = kind === 'booking'
      ? { booking_resource_ids: ids, booking_resource_id: ids[0] }
      : { field_quote_resource_ids: ids, field_quote_resource_id: ids[0] };
    void supabase.from('sales_plays').update(patch).eq('id', play.id).eq('status', 'staged').then(({ error: saveError }) => {
      if (saveError) setError('Could not update who is going.');
    });
  };

  useEffect(() => {
    if (!hasBooking || !roster.length) return;
    let cancel = false;
    void busyNames(supabase, businessId, bookingCrew, roster, play.booking_date, play.booking_time, play.booking_end_date, play.booking_end_time)
      .then((names) => {
        if (!cancel) setBookingNote(names.length ? `${names.join(' and ')} ${names.length > 1 ? 'are' : 'is'} already booked then.` : null);
      });
    return () => { cancel = true; };
  }, [hasBooking, supabase, businessId, bookingCrew, roster, play.booking_date, play.booking_time, play.booking_end_date, play.booking_end_time]);

  useEffect(() => {
    if (!hasFieldQuote || !roster.length) return;
    let cancel = false;
    void busyNames(supabase, businessId, visitCrew, roster, play.field_quote_date, play.field_quote_time, play.field_quote_end_date, play.field_quote_end_time)
      .then((names) => {
        if (!cancel) setVisitNote(names.length ? `${names.join(' and ')} ${names.length > 1 ? 'are' : 'is'} already booked then.` : null);
      });
    return () => { cancel = true; };
  }, [hasFieldQuote, supabase, businessId, visitCrew, roster, play.field_quote_date, play.field_quote_time, play.field_quote_end_date, play.field_quote_end_time]);

  const act = (action: 'send' | 'done' | 'skip') => {
    if (actingRef.current) return;
    actingRef.current = true;
    const status = action === 'send' ? 'sent' : action === 'done' ? 'done' : 'skipped';
    onActed(play.id, status);
    void actOnSalesPlay(play.id, action, action === 'skip' ? {} : {
      parts,
      ...(action === 'send' && edited ? { body, subject: subject || undefined } : {}),
      ...(on.booking ? { bookingResourceIds: bookingCrew } : {}),
      ...(on.field_quote ? { fieldQuoteResourceIds: visitCrew } : {}),
    }).then(() => {
      onSettled?.(play.id);
    }).catch((err) => {
      onFailed?.(play.id, err instanceof Error ? err.message : 'Did not go through.');
    });
  };

  // Header: tap opens the lead; press-and-hold (or right-click) opens quick actions.
  const headerRef = useRef<HTMLButtonElement>(null);
  const suppressClickRef = useRef(false);
  const openQuickActions = () => {
    const rect = headerRef.current?.getBoundingClientRect();
    if (!rect || !onQuickActions) return;
    void hapticMedium();
    onQuickActions({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
  };
  const longPress = useLongPress({
    onLongPress: () => {
      if (!onQuickActions) return;
      suppressClickRef.current = true;
      openQuickActions();
    },
    threshold: 500,
  });

  // The button says what it will do with what is ticked.
  const sends = on.proposal || on.booking || on.field_quote || (on.touch && isMessage);
  const primaryAction: 'send' | 'done' = sends && !isCall ? 'send' : 'done';
  const primaryLabel = !anyOn
    ? 'Nothing ticked'
    : sends && !isCall
      ? edited ? 'Send edited' : 'Send'
      : on.touch && isCall
        ? 'Done'
        : on.mark
          ? `Mark ${mark}`
          : 'Apply';
  const primaryClass = anyOn && on.mark && !on.touch && mark === 'lost' ? 'bg-gray-800 hover:bg-gray-900' : 'bg-emerald-600 hover:bg-emerald-700';
  const primaryDisabled = !anyOn || (on.touch && isMessage && (!body.trim() || (play.channel === 'email' && !subject.trim())));

  const draft = editing ? (
    <div className="space-y-1.5">
      {play.channel === 'email' ? (
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          className="w-full rounded-lg border border-gray-200 bg-gray-50/60 px-2.5 py-1.5 text-[12.5px] font-medium text-gray-900 outline-none focus:border-emerald-400 focus:bg-white"
        />
      ) : null}
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={Math.min(12, Math.max(3, body.split('\n').length + 1))}
        autoFocus
        className="w-full resize-y rounded-lg border border-gray-200 bg-gray-50/60 px-2.5 py-1.5 text-[12.5px] leading-snug text-gray-900 outline-none focus:border-emerald-400 focus:bg-white"
      />
    </div>
  ) : (
    // Collapsed shows two lines; a tap opens the whole draft to read. Editing is the pencil in the footer.
    <button
      type="button"
      onClick={() => setOpen((current) => !current)}
      className="block w-full text-left"
      title={open ? 'Collapse' : 'Read the whole draft'}
    >
      {play.channel === 'email' && subject ? (
        <span className={`block text-[12.5px] font-medium text-gray-800 leading-snug ${open ? '' : 'truncate'}`}>{subject}</span>
      ) : null}
      <span className={`block text-[12.5px] leading-snug whitespace-pre-line ${open ? 'text-gray-800' : 'text-gray-600 line-clamp-2'}`}>{body}</span>
    </button>
  );

  const covered = onCall || updating;

  return (
    <div
      className={`relative overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${covered ? 'border-zinc-950 bg-zinc-950' : 'border-gray-200/80 bg-white'}`}
      data-sales-play={play.id}
    >
      <div className={covered ? 'invisible pointer-events-none' : undefined} aria-hidden={covered || undefined}>
      {typeof item.leadScore === 'number' ? (
        <div className="px-2.5 pt-2">
          <LeadScoreThermometer score={item.leadScore} />
        </div>
      ) : null}
      <button
        ref={headerRef}
        type="button"
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }
          onOpenLead?.();
        }}
        onContextMenu={
          onQuickActions
            ? (e) => {
                e.preventDefault();
                suppressClickRef.current = true;
                openQuickActions();
              }
            : undefined
        }
        {...(onQuickActions ? longPress : {})}
        className="flex w-full items-center gap-2.5 rounded-t-xl px-2.5 pt-2.5 pb-1.5 text-left transition-colors hover:bg-gray-50/80 active:bg-gray-100 touch-manipulation"
        style={{
          WebkitTapHighlightColor: 'transparent',
          ...(onQuickActions ? { userSelect: 'none' as const, WebkitUserSelect: 'none' as const, WebkitTouchCallout: 'none' as const } : {}),
        }}
        title="Open lead"
      >
        <span className="relative shrink-0">
          <SalesFace size={30} title="Sales" />
          <span className={`absolute -bottom-0.5 -right-1 flex h-4 w-4 items-center justify-center rounded-full border border-white text-white ${mark === 'lost' ? 'bg-gray-700' : isWait ? 'bg-sky-600' : 'bg-emerald-600'}`}>
            <ChannelIcon className="h-2.5 w-2.5" aria-hidden />
          </span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 min-w-0">
            <span className="truncate text-[14px] font-semibold text-gray-900 leading-tight sm:text-[13px]">{item.title}</span>
            {/* Desktop: the reason sits at the right of the title line. */}
            <span className={`ml-auto hidden shrink-0 items-center gap-1 text-[11px] font-semibold leading-tight tabular-nums sm:inline-flex ${reason.className}`}>
              {reason.severity ? <AlertTriangle className="h-3 w-3 fill-current" aria-hidden /> : null}
              {reason.label}
            </span>
          </span>
          <span className="block truncate text-[11.5px] text-gray-500 leading-tight mt-0.5">
            {/* Phone: the reason leads the second line so the title keeps its width. */}
            <span className={`inline-flex items-center gap-1 font-semibold sm:hidden ${reason.className}`}>
              {reason.severity ? <AlertTriangle className="h-3 w-3 fill-current" aria-hidden /> : null}
              {reason.label}
              <span className="text-gray-400 font-normal">·</span>
            </span>{' '}
            {customer ? `${customer} · ` : ''}
            {byline}
          </span>
        </span>
      </button>

      {/* A move has no draft to read, so the reasoning is the body. */}
      {isMove && play.why ? <p className="px-2.5 pl-[42px] pb-1.5 text-[12.5px] leading-snug text-gray-700">{play.why}</p> : null}

      {/* The play, line by line. Untick anything to leave it alone. */}
      <ul className="space-y-1.5 px-2.5 pl-[42px]">
        {has.field_quote ? (
          <PartRow
            checked={on.field_quote}
            onToggle={() => toggle('field_quote')}
            label="Schedule the field quote"
            detail={roster.length ? scheduleLine(play.field_quote_label, visitCrew.map((id) => roster.find((person) => person.id === id)?.name || '')) : play.field_quote_label || undefined}
          >
            {roster.length ? (
              <CrewEditor
                ids={visitCrew}
                roster={roster}
                note={visitNote}
                onChange={(ids) => saveCrew('field_quote', ids)}
              />
            ) : null}
          </PartRow>
        ) : null}
        {has.booking ? (
          <PartRow
            checked={on.booking}
            onToggle={() => toggle('booking')}
            label="Create the booking"
            detail={roster.length ? scheduleLine(play.booking_label, bookingCrew.map((id) => roster.find((person) => person.id === id)?.name || '')) : play.booking_label || undefined}
          >
            {roster.length ? (
              <CrewEditor
                ids={bookingCrew}
                roster={roster}
                note={bookingNote}
                onChange={(ids) => saveCrew('booking', ids)}
              />
            ) : null}
            {play.booking_proposal_id ? (
              <button
                type="button"
                onClick={() => onOpenQuote?.(play.booking_proposal_id!)}
                className="mt-1 inline-flex h-7 items-center rounded-lg px-2 text-[12px] font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800"
              >
                Open quote
              </button>
            ) : null}
          </PartRow>
        ) : null}
        {has.proposal && play.proposal ? (
          <PartRow
            checked={on.proposal}
            onToggle={() => toggle('proposal')}
            label="Send proposal"
            detail={`${play.proposal.title} · ${moneyLabel(play.proposal.total)}`}
          >
            {play.proposal_basis ? <p className="text-[11.5px] leading-snug text-gray-500">{play.proposal_basis}</p> : null}
            <Link
              to={`/proposals?proposalId=${play.proposal.id}`}
              className="mt-1 inline-flex h-7 items-center gap-1 rounded-lg px-2 text-[12px] font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800"
            >
              <Pencil className="h-3 w-3" aria-hidden />
              Edit
            </Link>
          </PartRow>
        ) : null}
        {has.touch ? (
          <PartRow
            checked={on.touch}
            onToggle={() => toggle('touch')}
            label={`${channel.verb} ${firstName}`}
          >
            {isCall ? (
              <ul className="space-y-0.5">
                {play.talking_points.map((point, index) => (
                  <li key={index} className="flex gap-1.5 text-[12.5px] leading-snug text-gray-700">
                    <span className="text-emerald-600 shrink-0">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            ) : (
              draft
            )}
          </PartRow>
        ) : null}
        {has.stage ? (
          <PartRow checked={on.stage} onToggle={() => toggle('stage')}  label={`Move to ${play.move_to_stage}`} />
        ) : null}
        {has.follow_up ? (
          <PartRow checked={on.follow_up} onToggle={() => toggle('follow_up')}  label={`Follow up ${followUp}`} />
        ) : null}
        {has.mark ? (
          <PartRow checked={on.mark} onToggle={() => toggle('mark')}  label={`Mark ${mark}`} detail={mark === 'lost' ? play.lost_reason : null} />
        ) : null}
      </ul>

      {open ? (
        <div className="px-2.5 pl-[42px] mt-1.5 space-y-1">
          {play.why && !isMove ? <p className="text-[11.5px] text-gray-500 leading-snug">{play.why}</p> : null}
          {play.automation_note ? (
            <p className="flex items-start gap-1 text-[11.5px] text-amber-800 leading-snug" data-automation-note>
              <Zap className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" aria-hidden />
              <span>{play.automation_note}</span>
            </p>
          ) : null}
        </div>
      ) : play.automation_note ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-1 flex w-full items-center gap-1 px-2.5 pl-[42px] text-left text-[11px] text-amber-800 leading-tight"
          title="Automation note"
          data-automation-note
        >
          <Zap className="h-3 w-3 shrink-0 text-amber-500" aria-hidden />
          <span className="truncate">{play.automation_note}</span>
        </button>
      ) : null}
      {error ? <p className="mt-1 px-2.5 pl-[42px] text-[11px] text-red-600 leading-snug">{error}</p> : null}

      <div className="mt-1.5 flex items-center gap-1.5 px-2.5 pb-2 pl-[42px]">
        <span className="flex-1" />
        {open ? (
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setEditing(false);
            }}
            className="inline-flex h-7 items-center gap-0.5 rounded-lg px-2 text-[12px] font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            title="Collapse"
          >
            <ChevronUp className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
        {open && isMessage && on.touch && !editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex h-7 items-center gap-1 rounded-lg px-2.5 text-[12px] font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800"
          >
            <Pencil className="h-3 w-3" aria-hidden />
            Edit
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => { void talkAboutThis(); }}
          disabled={talking || updating}
          className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50"
          aria-label={`Talk to Sales about ${play.lead_name}`}
          title="Talk to Sales about this lead"
        >
          <AudioLines className="h-3.5 w-3.5" aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => act('skip')}
          className="inline-flex h-7 items-center rounded-lg px-2.5 text-[12px] font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          Skip
        </button>
        {isCall && on.touch && play.to_address ? (
          <a
            href={`tel:${play.to_address.replace(/[^\d+]/g, '')}`}
            className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-2.5 text-[12px] font-semibold text-emerald-800 hover:bg-emerald-50"
          >
            <Phone className="h-3.5 w-3.5" aria-hidden />
            Call
          </a>
        ) : null}
        <button
          type="button"
          onClick={() => act(primaryAction)}
          disabled={primaryDisabled}
          className={`inline-flex h-7 items-center gap-1.5 rounded-lg px-3 text-[12px] font-semibold text-white disabled:opacity-60 ${primaryClass}`}
        >
          {primaryLabel}
        </button>
      </div>
      </div>
      {covered ? <SalesCardVoiceSurface mode={onCall ? 'call' : 'updating'} /> : null}
    </div>
  );
}

/** Covers the lead card in place. The card underneath keeps its size. */
function SalesCardVoiceSurface({ mode }: { mode: 'call' | 'updating' }) {
  const { status, micMuted, toggleMicMuted, micStream, remoteStream, stopVoice } = useVoiceHelp();
  const connecting = status === 'connecting';
  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center bg-zinc-950 px-3"
      role="status"
      aria-live="polite"
    >
      {mode === 'call' ? (
        <div className="flex max-w-full items-center gap-1.5 rounded-full bg-neutral-950 py-1.5 pl-1.5 pr-1.5 shadow-2xl" aria-label={connecting ? 'Connecting voice' : 'Voice connected'}>
          <span className="relative flex h-9 w-9 shrink-0 items-center justify-center">
            <span className={connecting ? 'opacity-40' : undefined}>
              <SalesFace size={36} title="Sales" />
            </span>
            {connecting && (
              <span className="absolute inset-0 flex items-center justify-center" aria-hidden>
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              </span>
            )}
          </span>
          {connecting ? (
            <span className="px-1 text-[13px] font-medium text-white">Connecting</span>
          ) : (
            <VoiceMeter micStream={micStream} remoteStream={remoteStream} muted={micMuted} />
          )}
          <button
            type="button"
            onClick={toggleMicMuted}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white active:scale-95 ${micMuted ? 'bg-rose-500' : 'bg-neutral-800'}`}
            aria-label={micMuted ? 'Unmute microphone' : 'Mute microphone'}
            title={micMuted ? 'Unmute' : 'Mute'}
          >
            {micMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              stopVoice();
            }}
            className="relative z-20 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-500 text-white active:scale-95"
            aria-label="End voice conversation"
            title="End"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex max-w-full items-center gap-2.5 rounded-full bg-neutral-950 py-1.5 pl-1.5 pr-4 shadow-2xl" aria-label="Sales is updating this card">
          <span className="relative flex h-9 w-9 shrink-0 items-center justify-center">
            <span className="opacity-40">
              <SalesFace size={36} title="Sales" />
            </span>
            <span className="absolute inset-0 flex items-center justify-center" aria-hidden>
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            </span>
          </span>
          <span className="pr-1 text-[13px] font-medium text-white">Sales is updating this card</span>
        </div>
      )}
    </div>
  );
}
